/**
 * On Lock Support — Chat Widget
 *
 * Embed: <script defer src="https://cdn.onlockapparel.com.au/onlock-chat.js"></script>
 * Or paste inline in theme.liquid before </body>.
 *
 * Looks: black + gold On Lock aesthetic, mobile-first, bottom-right floating button.
 * Function: opens panel, chat with bot, persists conversation in localStorage,
 * sends customer.email if Shopify customer is logged in.
 */
(function () {
  if (window.__OnLockChatLoaded) return;
  window.__OnLockChatLoaded = true;

  // Production worker endpoint (deployed 2026-05-10):
  const API_URL = 'https://onlock-support-chat.joel-d32.workers.dev/chat';
  const BRAND_GOLD = '#C9A96E';
  const BG_DARK = '#0a0a0a';
  const STORAGE_KEY = 'onlock_chat_v1';

  // --- Detect logged-in Shopify customer if available ---
  function getCustomerEmail() {
    try {
      // Shopify exposes window.customer or window.Shopify.customer in some themes
      if (window.Shopify && window.Shopify.customer && window.Shopify.customer.email) {
        return window.Shopify.customer.email;
      }
      if (window.customer && window.customer.email) return window.customer.email;
      // Fallback: stored from past chat
      const stored = localStorage.getItem('onlock_chat_email');
      return stored || null;
    } catch { return null; }
  }

  // --- Build UI ---
  const css = `
    /* Floating button — filled black circle with gold icon */
    .ol-chat-btn {
      position: fixed; bottom: calc(20px + env(safe-area-inset-bottom, 0px)); right: 16px; z-index: 99999;
      width: 42px; height: 42px;
      background: ${BG_DARK}; border: 1px solid ${BRAND_GOLD}; border-radius: 50%;
      padding: 0; cursor: pointer;
      box-shadow: 0 2px 10px rgba(0,0,0,0.5);
      transition: transform 0.15s ease;
      display: flex; align-items: center; justify-content: center;
    }
    /* Phones: sit ABOVE the bottom bars (Joel 27 Sep 2026). The sticky Add To Bag bar is 50px and the
       cart drawer's floating Secure Checkout reaches ~63px up; at bottom:20px the bubble covered the
       right end of both, so a tap there opened chat instead of buying. */
    @media (max-width: 768px) {
      .ol-chat-btn { bottom: calc(76px + env(safe-area-inset-bottom, 0px)); }
    }
    /* Hidden while the cart drawer is open: it sat on the subtotal, and nobody needs chat mid-checkout. */
    body.ol-drawer-open .ol-chat-btn { display: none; }
    .ol-chat-btn:hover { transform: scale(1.08); }
    .ol-chat-btn:active { transform: scale(0.95); }
    .ol-chat-btn svg { display: block; }
    .ol-chat-btn.hidden { display: none; }

    /* Bot message markdown rendering */
    .ol-msg.bot a { color: ${BRAND_GOLD}; text-decoration: underline; word-break: break-all; }
    .ol-msg.bot strong { font-weight: 700; color: #fff; }

    /* Desktop panel: floating bottom-right */
    .ol-chat-panel {
      position: fixed; bottom: 90px; right: 20px; z-index: 99999;
      width: 380px; height: 580px; max-height: calc(100vh - 130px);
      background: ${BG_DARK}; border: 1px solid ${BRAND_GOLD};
      border-radius: 12px; box-shadow: 0 8px 40px rgba(0,0,0,0.5);
      display: none; flex-direction: column;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif;
      color: #f5f5f5;
      overflow: hidden;
    }
    .ol-chat-panel.open { display: flex; }

    .ol-chat-header {
      padding: 14px 16px; border-bottom: 1px solid #333; flex-shrink: 0;
      display: flex; justify-content: space-between; align-items: center;
      background: ${BG_DARK};
    }
    .ol-chat-header h3 { margin: 0; font-size: 14px; letter-spacing: 0.5px; color: ${BRAND_GOLD}; font-weight: 700; }
    .ol-chat-header span.sub { display: block; font-size: 11px; color: #888; margin-top: 2px; }
    .ol-chat-close {
      background: none; border: none; color: #888; font-size: 28px;
      cursor: pointer; padding: 4px 10px; line-height: 1;
      min-width: 44px; min-height: 44px;
      display: flex; align-items: center; justify-content: center;
    }
    .ol-chat-close:hover { color: ${BRAND_GOLD}; }

    .ol-chat-messages {
      flex: 1; overflow-y: auto; padding: 14px;
      display: flex; flex-direction: column; gap: 10px;
      -webkit-overflow-scrolling: touch;
    }
    .ol-msg {
      padding: 10px 14px; border-radius: 14px; max-width: 85%;
      word-wrap: break-word; line-height: 1.4; font-size: 14px;
    }
    .ol-msg.bot { background: #1a1a1a; color: #f5f5f5; align-self: flex-start; }
    .ol-msg.user { background: ${BRAND_GOLD}; color: ${BG_DARK}; align-self: flex-end; font-weight: 500; }
    .ol-msg.typing { font-style: italic; color: #888; background: transparent; padding: 4px 14px; }

    .ol-chat-form {
      display: flex; gap: 8px; padding: 12px;
      padding-bottom: calc(12px + env(safe-area-inset-bottom, 0px));
      border-top: 1px solid #333; flex-shrink: 0;
      background: ${BG_DARK};
    }
    .ol-chat-input {
      flex: 1; padding: 12px 14px; border-radius: 22px;
      border: 1px solid #333; background: #1a1a1a; color: #f5f5f5;
      font-size: 16px; /* 16px stops iOS auto-zoom on focus */
      outline: none;
      -webkit-appearance: none;
    }
    .ol-chat-input:focus { border-color: ${BRAND_GOLD}; }
    .ol-chat-send {
      padding: 12px 18px; border-radius: 22px; border: none;
      background: ${BRAND_GOLD}; color: ${BG_DARK}; font-weight: 700;
      cursor: pointer; font-size: 13px; letter-spacing: 0.5px;
      min-width: 60px;
    }
    .ol-chat-send:disabled { opacity: 0.4; cursor: not-allowed; }

    /* Mobile: full screen takeover */
    @media (max-width: 600px) {
      .ol-chat-panel {
        position: fixed;
        top: 0; left: 0; right: 0; bottom: 0;
        width: 100vw; height: 100vh; height: 100dvh;
        max-height: none; border-radius: 0; border: none;
      }
      .ol-chat-panel.open { display: flex; }
      .ol-chat-header {
        padding: calc(12px + env(safe-area-inset-top, 0px)) 16px 12px 16px;
        border-bottom: 1px solid ${BRAND_GOLD};
      }
      /* Hide button when panel is open on mobile */
      body.ol-chat-locked { overflow: hidden; position: fixed; width: 100%; }
    }
  `;
  const style = document.createElement('style');
  style.textContent = css;
  document.head.appendChild(style);

  // Floating button — chat bubble SVG inside filled black circle
  const btn = document.createElement('button');
  btn.className = 'ol-chat-btn';
  btn.setAttribute('aria-label', 'Chat with On Lock Support');
  btn.innerHTML = `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="${BRAND_GOLD}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  </svg>`;
  document.body.appendChild(btn);

  // Panel
  const panel = document.createElement('div');
  panel.className = 'ol-chat-panel';
  panel.innerHTML = `
    <div class="ol-chat-header">
      <div>
        <h3>ON LOCK SUPPORT</h3>
        <span class="sub">Usually replies in seconds</span>
      </div>
      <button class="ol-chat-close" aria-label="Close">&times;</button>
    </div>
    <div class="ol-chat-messages"></div>
    <form class="ol-chat-form">
      <input class="ol-chat-input" placeholder="Type a message…" autocomplete="off" />
      <button class="ol-chat-send" type="submit">SEND</button>
    </form>
  `;
  document.body.appendChild(panel);

  const messagesEl = panel.querySelector('.ol-chat-messages');
  const formEl = panel.querySelector('.ol-chat-form');
  const inputEl = panel.querySelector('.ol-chat-input');
  const sendEl = panel.querySelector('.ol-chat-send');

  // --- State ---
  let messages = [];
  let customerEmail = getCustomerEmail();

  function load() {
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
      if (stored && stored.messages) messages = stored.messages;
    } catch {}
  }

  function save() {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ messages })); } catch {}
  }

  // --- Markdown renderer (XSS-safe) ---
  // Renders **bold**, URLs, emails, and \n line breaks
  function renderMarkdown(text) {
    // 1. Escape HTML first to prevent XSS
    const tmp = document.createElement('div');
    tmp.textContent = text;
    let html = tmp.innerHTML;
    // 2. URLs → clickable links (must run before emails to avoid double-wrap)
    html = html.replace(/(https?:\/\/[^\s<]+[^\s<.,!?)])/g, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>');
    // 3. Bare emails → mailto links (skip if already inside an <a>)
    html = html.replace(/(^|[^"=>])([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g, '$1<a href="mailto:$2">$2</a>');
    // 4. **bold** → <strong>
    html = html.replace(/\*\*([^*\n]+?)\*\*/g, '<strong>$1</strong>');
    // 5. Newlines → <br>
    html = html.replace(/\n/g, '<br>');
    return html;
  }

  function render() {
    messagesEl.innerHTML = '';
    if (!messages.length) {
      addBotBubble("Yo legend, how can we help? Sizing, shipping, returns, order tracking. Fire away.");
      return;
    }
    for (const m of messages) {
      const div = document.createElement('div');
      div.className = `ol-msg ${m.role === 'user' ? 'user' : 'bot'}`;
      if (m.role === 'user') {
        div.textContent = m.content;
      } else {
        div.innerHTML = renderMarkdown(m.content);
      }
      messagesEl.appendChild(div);
    }
    messagesEl.scrollTop = messagesEl.scrollHeight;
  }

  function addBotBubble(text) {
    const div = document.createElement('div');
    div.className = 'ol-msg bot';
    div.innerHTML = renderMarkdown(text);
    messagesEl.appendChild(div);
    messagesEl.scrollTop = messagesEl.scrollHeight;
  }

  function addUserBubble(text) {
    const div = document.createElement('div');
    div.className = 'ol-msg user';
    div.textContent = text;
    messagesEl.appendChild(div);
    messagesEl.scrollTop = messagesEl.scrollHeight;
  }

  function showTyping() {
    const div = document.createElement('div');
    div.className = 'ol-msg typing';
    div.textContent = 'OL Support is typing…';
    div.id = 'ol-typing';
    messagesEl.appendChild(div);
    messagesEl.scrollTop = messagesEl.scrollHeight;
  }

  function hideTyping() {
    const t = document.getElementById('ol-typing');
    if (t) t.remove();
  }

  async function sendMessage(text) {
    if (!text.trim()) return;
    messages.push({ role: 'user', content: text });
    addUserBubble(text);
    save();
    inputEl.value = '';
    sendEl.disabled = true;
    showTyping();

    try {
      const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages, customer_email: customerEmail }),
      });
      const data = await res.json();
      hideTyping();
      const reply = data.reply || "Yo, I hit a snag on my end. Try again or shoot us an email at support@onlockapparel.com.au.";
      messages.push({ role: 'assistant', content: reply });
      addBotBubble(reply);
      save();
    } catch (e) {
      hideTyping();
      const fallback = "Yo brother, the line dropped on my end. Shoot us an email at support@onlockapparel.com.au and we'll sort you out.";
      messages.push({ role: 'assistant', content: fallback });
      addBotBubble(fallback);
      save();
    } finally {
      sendEl.disabled = false;
      inputEl.focus();
    }
  }

  // --- Wire up ---
  function openPanel() {
    panel.classList.add('open');
    btn.classList.add('hidden');
    if (window.matchMedia('(max-width: 600px)').matches) {
      document.body.classList.add('ol-chat-locked');
    }
    if (!messages.length) render();
    setTimeout(() => inputEl.focus(), 100);
  }
  function closePanel() {
    panel.classList.remove('open');
    btn.classList.remove('hidden');
    document.body.classList.remove('ol-chat-locked');
  }
  btn.addEventListener('click', openPanel);
  panel.querySelector('.ol-chat-close').addEventListener('click', closePanel);
  formEl.addEventListener('submit', (e) => {
    e.preventDefault();
    sendMessage(inputEl.value);
  });

  load();
  render();
})();
